/* eslint-disable consistent-return */
import { useDropzone } from 'react-dropzone';
import React, { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { Iconify } from '../iconify'; // Adjust this import as per your setup

export function UploadBox({
  value,
  onDrop,
  placeholder,
  accept,
  multiple = false,
  disabled,
  error,
  className,
  sx,
}) {
  const [hovered, setHovered] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    disabled: disabled,
    accept,
    multiple,
    onDrop: (acceptedFiles) => {
      if (onDrop) onDrop(acceptedFiles);
    },
  });

  const hasError = isDragReject || error;

  const isImage = (() => {
    if (typeof value === 'string') return /\.(jpeg|jpg|png|gif|webp)$/i.test(value);
    if (value instanceof File) return value.type.startsWith('image/');
    return false;
  })();

  const isPDF = (() => {
    if (typeof value === 'string') return /\.pdf$/i.test(value);
    if (value instanceof File) return value.type === 'application/pdf';
    return false;
  })();

  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (typeof value === 'string') {
      setPreviewUrl(value);
    } else {
      setPreviewUrl('');
    }
  }, [value]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = previewUrl;
    link.target = '_blank';
    link.download = 'invoice0.pdf';
    link.click();
  };

  return (
    <>
      <Box
        {...getRootProps()}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={className}
        sx={{
          width: 96,
          height: 96,
          borderRadius: 1,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #ccc',
          bgcolor: '#fafafa',
          ...(isDragActive && { opacity: 0.72 }),
          ...(disabled && { opacity: 0.5, pointerEvents: 'none' }),
          ...(hasError && {
            borderColor: 'red',
            bgcolor: '#ffeaea',
          }),
          ...sx,
        }}
      >
        <input {...getInputProps()} />

        {previewUrl ? (
          <>
            {isImage && (
              <Box
                component="img"
                src={previewUrl}
                alt="preview"
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            )}

            {isPDF && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  p: 1,
                }}
              >
                <Iconify icon="eva:file-text-outline" width={28} />
                <Typography variant="caption" noWrap textAlign="center">
                  {value?.name || 'PDF File'}
                </Typography>
              </Box>
            )}

            {!isImage && !isPDF && (
              <Typography variant="caption" textAlign="center" px={1}>
                {value?.name || 'File'}
              </Typography>
            )}

            {hovered && (isImage || isPDF) && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isImage) {
                    setOpenModal(true);
                  } else {
                    handleDownload();
                  }
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(87, 87, 87, 0.4)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                }}
                aria-label="preview file"
              >
                <Iconify icon="eva:eye-fill" width={20} />
              </IconButton>
            )}
          </>
        ) : (
          <Typography variant="caption" textAlign="center" px={1}>
            {placeholder || (
              <Iconify
                icon="eva:cloud-upload-fill"
                width={28}
                sx={{
                  color: 'text.secondary',
                }}
              />
            )}
          </Typography>
        )}
      </Box>

      {/* Modal for Preview */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            zIndex: 2,
          }}
        >
          {isImage && (
            <Box
              component="img"
              src={previewUrl}
              alt="Full preview"
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}

          {/* {isPDF && (
          // <PdfViewer fileUrl={previewUrl} />
          )} */}
        </Box>
      </Modal>
    </>
  );
}
